import {
  ButtonProps,
  Heading,
  Icon,
  List,
  Text,
  VStack,
} from "@chakra-ui/react";
import { BsQuestionCircle } from "react-icons/bs";

import { RoundedButton } from "@/components/rounded-button";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "@/components/ui/dialog";

export const PointsFaqModal = (buttonStyleProps?: ButtonProps) => {
  return (
    <DialogRoot size="lg" placement="center">
      <DialogTrigger asChild>
        <RoundedButton {...buttonStyleProps}>
          <Icon as={BsQuestionCircle} boxSize={4} />
          Plume Points FAQ
        </RoundedButton>
      </DialogTrigger>
      <DialogContent
        maxH="80vh"
        overflowY="auto"
        maxW={{ base: "100%", md: "540px" }}
        fontFamily="Lufga, sans-serif"
        css={{
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0, 0, 0, 0.2)",
            borderRadius: "10px",
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "rgba(0, 0, 0, 0.3)",
            backgroundClip: "content-box",
          },
          "&::-webkit-scrollbar-thumb:active": {
            background: "rgba(0, 0, 0, 0.4)",
            backgroundClip: "content-box",
          },
          // Firefox scrollbar styling
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
        }}
      >
        <DialogHeader
          display="unset"
          position="sticky"
          top={0}
          bg="white"
          borderBottom="1px solid"
          borderColor="border.subtle"
          mb={4}
          py={8}
        >
          <Heading
            fontSize={{ base: "3xl", md: "40px" }}
            textAlign="center"
            fontWeight={700}
            mb={0}
          >
            Plume Points FAQ
          </Heading>
          <DialogCloseTrigger
            rounded="full"
            border="1px solid"
            borderColor="border"
            position="absolute"
            top={4}
            right={4}
            zIndex={10}
          />
        </DialogHeader>
        <DialogBody spaceY={6} px={8} lineHeight={1.25}>
          <VStack alignItems="stretch" spaceY={6}>
            <VStack
              alignItems="stretch"
              spaceY={3}
              borderBottom="1px solid"
              borderColor="border"
              pb={6}
            >
              <Heading fontSize="18px" fontWeight={500}>
                What are Plume Points?
              </Heading>
              <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                Plume Points <Text as="strong">(PP)</Text> are the official
                on-chain engagement score of the Plume ecosystem. Every
                meaningful action, swapping, staking, supplying liquidity,
                referring friends - earns PP. The higher your total, the larger
                your slice of Season 2 rewards.
              </Text>
            </VStack>

            <VStack
              alignItems="stretch"
              spaceY={3}
              borderBottom="1px solid"
              borderColor="border"
              pb={6}
            >
              <Heading fontSize="18px" fontWeight={500}>
                Why do Plume Points matter?
              </Heading>
              <List.Root gap={1} pl={4}>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      Airdrop Eligibility:
                    </Text>{" "}
                    PP is the primary metric for Season 2 token distribution.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      Sky Society Boosts:
                    </Text>{" "}
                    Higher tiers unlock Plume Point drops, early access perks,
                    and role-based events.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      Ecosystem Priority:
                    </Text>{" "}
                    Future partner launches, NFT drops, and closed betas will
                    favor wallets with proven PP activity.
                  </Text>
                </List.Item>
              </List.Root>
            </VStack>

            <VStack
              alignItems="stretch"
              spaceY={3}
              borderBottom="1px solid"
              borderColor="border"
              pb={6}
            >
              <Heading fontSize="18px" fontWeight={500}>
                How can I earn Plume Points?
              </Heading>
              <Text fontSize="18px" color="fg.muted" mb={2} fontWeight={500}>
                You earn PP whenever you generate real activity on Plume. The
                biggest buckets are:
              </Text>
              <List.Root gap={1} pl={4}>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      On-chain usage:
                    </Text>{" "}
                    Bridge, swap, stake, try new dapps, and keep using your
                    favorite dapps.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      TVL:
                    </Text>{" "}
                    Provide TVL and liquidity - move up the TVL tiers and
                    maintain streaks for more PP.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      Volume:
                    </Text>{" "}
                    Trade counts and volumes, including consistent weekly
                    activity.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      Level Up:
                    </Text>{" "}
                    Complete the weekly Level Up quests in the Portal for bonus
                    PP (new quests unlock at a regular cadence, be sure to check
                    back every week).
                  </Text>
                </List.Item>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      Referrals:
                    </Text>{" "}
                    Your top 10 invitees earn you 10% of the PP they earn.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      Daily spin:
                    </Text>{" "}
                    Spin daily for a chance at PP winnings.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      Consistency bonus:
                    </Text>{" "}
                    Wallets over-week, over-month, receive a hidden activity
                    boost.
                  </Text>
                </List.Item>
              </List.Root>
              <Text
                fontSize="18px"
                color="fg.muted"
                fontStyle="italic"
                mt={3}
                fontWeight={500}
              >
                Multipliers behind each bucket are dynamic and may change,
                especially during special events, to keep the system fair and
                sybil-resistant.
              </Text>
            </VStack>

            <VStack
              alignItems="stretch"
              spaceY={3}
              borderBottom="1px solid"
              borderColor="border"
              pb={6}
            >
              <Heading fontSize="18px" fontWeight={500}>
                When do Points update?
              </Heading>
              <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                Points settle at <Text as="strong">00:00 UTC</Text> daily. On
                chain snapshots run hourly. If you see a short delay on the
                frontend, your backend total is still counting.
              </Text>
            </VStack>

            <VStack
              alignItems="stretch"
              spaceY={3}
              borderBottom="1px solid"
              borderColor="border"
              pb={6}
            >
              <Heading fontSize="18px" fontWeight={500}>
                Why isn&apos;t the exact points logic public?
              </Heading>
              <Text fontSize="18px" color="fg.muted" mb={3} fontWeight={500}>
                Plume prioritizes organic exploration. Publishing exact point
                tables invites botnets to farm solely for airdrop extraction.
                Keeping multipliers dynamic lets us:
              </Text>
              <List.Root gap={1} as="ol" pl={4}>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    Spotlight new dApps without manual farming scripts.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    Nerf Sybil patterns in real time.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    Reward genuine, long-term users.
                  </Text>
                </List.Item>
              </List.Root>
              <Text
                fontSize="18px"
                color="fg.muted"
                fontStyle="italic"
                mt={3}
                fontWeight={500}
              >
                Multipliers and bonus weights are subject to change, especially
                around special events (e.g. RWA &quot;double-XP&quot; weeks or
                partner spotlights).
              </Text>
            </VStack>

            <VStack
              alignItems="stretch"
              spaceY={3}
              borderBottom="1px solid"
              borderColor="border"
              pb={6}
            >
              <Heading fontSize="18px" fontWeight={500}>
                Can I lose Plume Points?
              </Heading>
              <List.Root gap={1} pl={4}>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    Normal activity never subtracts PP. Sometimes users have no
                    TVL, staking or other metrics on the leaderboard but still
                    have high PP; this is because they earned the points based
                    on historical behavior and don&apos;t lose them. They
                    won&apos;t keep earning as many points going forward until
                    they deposit again.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="strong">Penalties</Text> apply only to confirmed
                    sybil wallets, exploiters, or reversed transactions.
                  </Text>
                </List.Item>
              </List.Root>
            </VStack>

            <VStack alignItems="stretch" spaceY={3}>
              <Heading fontSize="18px" fontWeight={500}>
                I think my Points look off, what should I do?
              </Heading>
              <List.Root gap={1} pl={4}>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      Step 1:
                    </Text>{" "}
                    Wait for the next 00:00 UTC refresh.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      Step 2:
                    </Text>{" "}
                    If still mismatched, open a Support Ticket in Discord with
                    your wallet + tx hash.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text fontSize="18px" color="fg.muted" fontWeight={500}>
                    <Text as="span" fontWeight={700}>
                      Step 3:
                    </Text>{" "}
                    Mods will escalate to the Points reconciliation board.
                  </Text>
                </List.Item>
              </List.Root>
            </VStack>
          </VStack>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
};
